import React from 'react';
import styled from 'styled-components';
import { FaInstagram, FaFacebook, FaEtsy } from 'react-icons/fa';
import duckImage from '../images/duck.png';
import mouseImage from '../images/mouse.jpeg';
import duckyImage from '../images/ducky.jpeg';
import dollImage from '../images/doll.jpeg';
import highlandCowImage from '../images/HighlandCow.png';
import largePenguinImage from '../images/LargePenguin.png';
import mooBuddy from '../images/moobuddy.jpeg';

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 6rem auto 4rem;
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  color: #7A4988;
  font-size: 2.4rem;
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
`;

// 2-up grid that wraps + equal-height rows
const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  align-items: stretch;
  grid-auto-rows: 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Equal-height product tile
const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
  padding: 1.5rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.75);
  box-shadow: 0 6px 18px rgba(122, 73, 136, 0.12);
  backdrop-filter: blur(6px);
  height: 100%;
`;

const ProductTitle = styled.h2`
  color: #7A4988;
  font-size: 1.8rem;
  margin: 0;
  font-family: 'Dancing Script', cursive;
`;

/**
 * ✅ SHOW THE WHOLE IMAGE (no cropping)
 * - Use object-fit: contain so the entire image is visible
 * - Use a fixed image "frame" height so all cards stay uniform
 * - Add a subtle background behind transparent/odd-aspect images
 */
const ProductImage = styled.img`
  width: 100%;
  max-width: 300px;
  height: 260px;              /* keeps card layouts consistent */
  object-fit: contain;         /* 👈 shows the whole image */
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;             /* gives breathing room */
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(122, 73, 136, 0.15);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.03);
  }
`;

// Clamp description so long text doesn't change card height
const ProductDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #444;
  width: 100%;
  margin: 0;
  font-family: 'Poppins', sans-serif;

  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BuyButton = styled.a`
  margin-top: auto;
  background: #FF85C0;
  color: #ffffff;
  padding: 0.8rem 2rem;
  font-size: 1.2rem;
  line-height: 1.5;
  text-decoration: none;
  border-radius: 25px;
  display: inline-block;
  transition: all 0.3s ease;
  box-shadow: 0 3px 8px rgba(255, 170, 215, 0.4);

  &:hover {
    background: #7A4988;
    transform: translateY(-3px);
    box-shadow: 0 5px 12px rgba(255, 170, 215, 0.5);
  }
`;

// Footer
const Footer = styled.footer`
  padding: 3rem 2rem 1.5rem;
  background-color: #7A4988;
  color: #fff;
  font-family: 'Poppins', sans-serif;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 30px;
    height: 2px;
    background-color: #FFAAD7;
  }
`;

const FooterLink = styled.a`
  color: #F3E3F7;
  text-decoration: none;
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateX(3px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SocialIcon = styled.a`
  color: #F3E3F7;
  font-size: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateY(-3px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
`;

function ReadyToShip() {
  return (
    <>
      <PageContainer>
        <Title>Ready to Ship</Title>

        <ProductsGrid>
          {/* Mallard Duck Lovey */}
          <ProductCard>
            <ProductTitle>Mallard Duck Lovey</ProductTitle>
            <ProductImage src={duckImage} alt="Mallard Duck Lovey" />
            <ProductDescription>
              These cute mallard duck baby lovey snuggler comfort friends are so cute and soft! Perfect for a boy nursery! They are made with the softest yarn!
            </ProductDescription>
            <BuyButton
              href="https://square.link/u/mygUkAIf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>

          {/* If You Give A Mouse A Cookie Book Buddy Set */}
          <ProductCard>
            <ProductTitle>If You Give A Mouse A Cookie Book Buddy Set</ProductTitle>
            <ProductImage
              src={mouseImage}
              alt="If You Give A Mouse A Cookie Book Buddy Set"
            />
            <ProductDescription>
              A sweet book buddy set inspired by If You Give a Mouse a Cookie — perfect for storytime cuddles and gifting!
            </ProductDescription>
            <BuyButton
              href="https://square.link/u/gofdwNGP"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>

          {/* Jumbo Mallard Duck Plushie */}
          <ProductCard>
            <ProductTitle>Jumbo Mallard Duck Plushie</ProductTitle>
            <ProductImage src={duckyImage} alt="Jumbo Mallard Duck Plushie" />
            <ProductDescription>
              Chubby xl jumbo crochet duck plushie!! He is a whopping 21-22 inches from feet to head, perfectly stuffed, and made with the softest yarn and stuffing there is!
            </ProductDescription>
            <BuyButton
              href="https://square.link/u/dnUzPLuc"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>

          {/* Fairy Ballerina Plushie */}
          <ProductCard>
            <ProductTitle>Fairy Ballerina Plushie</ProductTitle>
            <ProductImage src={dollImage} alt="Fairy Ballerina Plushie" />
            <ProductDescription>
              The sweetest and softest crochet fairy ballerina doll! Perfect for a girlie girl in your life or a sweet baby shower gift.
            </ProductDescription>
            <BuyButton
              href="https://square.link/u/z83T3Pta"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>

          {/* Highland Cow Plushie */}
          <ProductCard>
            <ProductTitle>Highland Cow Plushie</ProductTitle>
            <ProductImage src={highlandCowImage} alt="Highland Cow Plushie" />
            <ProductDescription>
              These cute cow baby lovey snuggler comfort friends are so cute and so soft! Perfect for a western or farm animal themed nursery!
            </ProductDescription>
            <BuyButton
              href="https://square.link/u/ZJ0qSG2b"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>

          {/* Large Penguin Plushie */}
          <ProductCard>
            <ProductTitle>Large Penguin Plushie</ProductTitle>
            <ProductImage src={largePenguinImage} alt="Large Penguin Plushie" />
            <ProductDescription>
              Chubby xl jumbo crochet penguin plushie! She is a whopping 20-21 inches from feet to head, perfectly stuffed.
            </ProductDescription>
            <BuyButton
              href="https://square.link/u/k2pRNYRI"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>

          {/* Click-Clack Moo Book Buddy Gift Set*/}
          <ProductCard>
            <ProductTitle>Click-Clack Moo Book Buddy Gift Set</ProductTitle>
            <ProductImage src={mooBuddy} alt="Click-Clack Moo Book Buddy Gift Set" />
            <ProductDescription>
              Make story time MOOre fun with this Click Clack Moo Book Buddy Gift Set!
              This sweet, farm-themed bundle pairs a handmade crochet plushie with the 
              beloved children’s book.

            </ProductDescription>
            <BuyButton
              href="https://square.link/u/jQ7544hq"
              target="_blank"
              rel="noopener noreferrer"
            >
              Buy Now
            </BuyButton>
          </ProductCard>
        </ProductsGrid>
      </PageContainer>

        {/* Footer */}
              <Footer>
                <FooterContent>
                  <FooterSection>
                    <FooterTitle>Quick Links</FooterTitle>
                    <FooterLink href="/">Home</FooterLink>
                    <FooterLink href="/custom-order">Custom Orders</FooterLink>
                    <FooterLink href="/faq">FAQ</FooterLink>
                  </FooterSection>
        
                  <FooterSection>
                    <FooterTitle>Policies</FooterTitle>
                    <FooterLink href="/shipping">Shipping</FooterLink>
                    <FooterLink href="/returns">Returns</FooterLink>
                    <FooterLink href="/privacy">Privacy Policy</FooterLink>
                    <FooterLink href="/terms">Terms of Service</FooterLink>
                  </FooterSection>
        
                  <FooterSection>
                    <FooterTitle>Connect With Us</FooterTitle>
                    <p>Follow us for updates and behind-the-scenes content!</p>
                    <SocialLinks>
                      <SocialIcon href="https://www.instagram.com/lavenderandpalmcrochet/">
                        <FaInstagram />
                      </SocialIcon>
                      <SocialIcon href="https://www.facebook.com/people/Lavender-and-Palm-Crochet/61566023416208/#">
                        <FaFacebook />
                      </SocialIcon>
                      <SocialIcon href="https://lavenderandpalm.etsy.com">
                        <FaEtsy />
                      </SocialIcon>
                    </SocialLinks>
                  </FooterSection>
                </FooterContent>
        
                <Copyright>
                  © {new Date().getFullYear()} Lavender & Palm Co. All rights reserved.
                </Copyright>
              </Footer>
    </>
  );
}

export default ReadyToShip;